import { useAuthentication } from '@pflegenavi/frontend/authentication';
import {
  makeApiProvider,
  useApiQuery,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import type {
  EmployeeProfilePhoenix,
  UpdateFamilyMemberPhoenixDto,
  EmployeeCardsResponse,
  EmployeeCardsResponseWithDates,
  EmployeeCardPinResponse,
} from './api';
import type { IUserProfilePhoenixApi } from './api';
import { UserProfilePhoenixApi } from './api';
import {
  USER_PROFILE_PHOENIX_KEY,
  EMPLOYEE_CARDS_KEY,
  EMPLOYEE_CARD_PIN_KEY,
} from './queryKeys';
import { invalidateResident } from '../resident/queryKeys';

const ApiContext = createContext<IUserProfilePhoenixApi | undefined>(undefined);
const {
  useApi: useUserProfilePhoenixApi,
  ApiProvider: UserProfilePhoenixApiProvider,
} = makeApiProvider({
  name: 'UserProfilePhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new UserProfilePhoenixApi(tenantId, auth, apiUrl),
});

export type {
  IUserProfilePhoenixApi,
  UpdateFamilyMemberPhoenixDto,
  UpdateFamilyMemberPhoenixResponse,
  EmployeeProfilePhoenix,
  EmployeeCard,
  EmployeeCardsResponse,
  EmployeeCardPinResponse,
  CardType,
  CardStatus,
  LimitRenewFrequency,
  CashWithdrawalLimits,
  FamilyMemberRelationshipPhoenix,
} from './api';
export {
  ApiContext as UserProfilePhoenixApiContext,
  UserProfilePhoenixApiProvider,
  useUserProfilePhoenixApi,
};
export {
  isCardFinal,
  isCardTerminated,
  getStatusColor,
  formatRenewFrequency,
  getContextualLimitLabel,
  getEffectiveCashWithdrawalLimits,
  getNextResetDate,
  canShowCardPin,
  getLimitUsage,
  getSpentAmount,
} from './cardUtils';

export const useEmployeeProfilePhoenix = (
  options: Omit<
    UseQueryOptions<EmployeeProfilePhoenix, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<EmployeeProfilePhoenix, unknown> => {
  const { user } = useAuthentication();

  const api = useUserProfilePhoenixApi(options.enabled === false);

  return useApiQuery(
    () => api,
    USER_PROFILE_PHOENIX_KEY(user?.userId ?? ''),
    (api) => {
      return api!.getEmployeeProfile();
    },
    {
      ...options,
      staleTime: options?.staleTime ?? Infinity,
    }
  );
};

export const useEmployeeProfilePhoenixSuspense = (
  options: Omit<
    UseQueryOptions<EmployeeProfilePhoenix, unknown>,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<EmployeeProfilePhoenix, unknown> => {
  const { user } = useAuthentication();

  const api = useUserProfilePhoenixApi(options.enabled === false);

  return useSuspenseApiQuery(
    () => api,
    USER_PROFILE_PHOENIX_KEY(user?.userId ?? ''),
    (api) => {
      return api!.getEmployeeProfile();
    },
    {
      ...options,
      staleTime: options?.staleTime ?? Infinity,
    }
  );
};

export const useEmployeeCards = (
  options?: Omit<
    UseQueryOptions<EmployeeCardsResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<EmployeeCardsResponseWithDates, unknown> => {
  const api = useUserProfilePhoenixApi(options?.enabled === false);

  return useApiQuery(
    () => api,
    EMPLOYEE_CARDS_KEY(),
    (api) => {
      return api!.getEmployeeCards();
    },
    {
      ...options,
      select: (data) => ({
        ...data,
        data: data.data.map((card) => ({
          ...card,
          issued_at: new Date(card.issued_at),
          expires_at: new Date(card.expires_at),
          terminated_at: card.terminated_at
            ? new Date(card.terminated_at)
            : null,
          limit_renew_date: card.limit_renew_date
            ? new Date(card.limit_renew_date)
            : null,
          limit_period_end: card.limit_period_end
            ? new Date(card.limit_period_end)
            : null,
        })),
      }),
      staleTime: options?.staleTime ?? Infinity,
    }
  );
};

/**
 * Fetches the PIN of one of the current employee's cards. Nothing is requested
 * until `enabled` is true, and the PIN is dropped from the cache as soon as the
 * last consumer unmounts, so it never outlives the dialog that revealed it.
 *
 * @example
 * const { data: pin } = useEmployeeCardPin(card.id, revealed);
 */
export const useEmployeeCardPin = (
  cardId: string,
  enabled: boolean
): UseQueryResult<string, unknown> => {
  const api = useUserProfilePhoenixApi(!enabled);

  return useApiQuery(
    () => api,
    EMPLOYEE_CARD_PIN_KEY(cardId),
    (api) => api!.getEmployeeCardPin({ params: { cardId } }),
    {
      enabled,
      select: (response: EmployeeCardPinResponse) => response.data.pin,
      gcTime: 0,
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
};

export const useUpdateFamilyMemberByEmployeePhoenix = (
  residentId: string
): UseMutationResult<
  UpdateFamilyMemberPhoenixDto,
  unknown,
  { familyMemberId: string; data: UpdateFamilyMemberPhoenixDto }
> => {
  const api = useUserProfilePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    UpdateFamilyMemberPhoenixDto,
    unknown,
    { familyMemberId: string; data: UpdateFamilyMemberPhoenixDto }
  >({
    mutationKey: ['update_family_member_phoenix'],
    mutationFn: ({ familyMemberId, data }) =>
      api?.updateFamilyMemberByEmployee({
        params: { familyMemberId },
        body: data,
      }) as unknown as Promise<UpdateFamilyMemberPhoenixDto>,
    onSuccess: async () => {
      return await invalidateResident(queryClient, residentId, undefined);
    },
  });
};
